import {Component, Input, ViewChild} from '@angular/core';
import {PageEvent} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort, Sort} from '@angular/material/sort';
import {FinanceReportsService} from '../../service/finance-reports.service';
import {ChartOptions} from 'chart.js';
import {ChartOptionsService} from 'src/app/shared/components/chart/chart-options.service';
import {ChartDataSet} from 'src/app/shared/components/chart/chart.component';
import {AzureAuthService} from 'src/infrastructure/services/azure-auth.service';
import {FilterService} from 'src/app/shared/service/filter/filter.service';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {TripProfitability} from '../../model/trip-profitability';
import {TripProfitabilityResponse} from '../../model/trip-profitability-response';
import {FinanceReportFilterResult} from 'src/app/shared/model/finance-report-filter-result';
import moment from 'moment';
import {ExportGridService} from '../../../shared/service/files/export-grid.service';
import {DatePipe} from '@angular/common';

@Component({
    selector: 'app-trip-profitability-with-agency',
    templateUrl: './trip-profitability-with-agency.component.html',
    standalone: false
})

@AutoUnsubscribe()
export class TripProfitabilityWithAgencyComponent {
  @Input() filter!: FinanceReportFilterResult;

  pagination: Partial<PageEvent & {totalItemCount?: number}> = {};

  @ViewChild(MatSort, {static: false}) public sort!: MatSort;
  @ViewChild(MatSort)
  public set matSort(ms: MatSort) {
    this.sort = ms;
    this.dataSourceDirector.sort = this.sort;
  }

  tripProfitabilityPieChartOptions: ChartOptions;
  tripProfitabilityPieChartData!: ChartDataSet;
  public isUserHasDirectorRole: boolean = false;
  public tripProfitabilityDirectorReportFilter: TripProfitability = {} as TripProfitability;
  public dataSourceDirector: MatTableDataSource<TripProfitability> = new MatTableDataSource<TripProfitability>([]);
  displayedColumnsDirector = [
    'tripReference',
    'dateCreated',
    'advisorName',
    'agentSplit',
    'agencySplit',
    'marginPercentage',
    'adxBookingNumber'
  ];
  tripProfitabilityDirectorReportData: TripProfitability[] = [];

  private pieChartDataInitialized = false;
  private totalMarginRatios = { below25: 0, above25: 0 };

  constructor(
    private azureAuthService: AzureAuthService,
    private financeReportsService: FinanceReportsService,
    private exportGridService: ExportGridService,
    private datePipe: DatePipe,
    private chartOptions: ChartOptionsService,
    private filters: FilterService
    ) {
      this.isUserHasDirectorRole = this.azureAuthService.checkUserAzureTokenHasDirectorRole();
      this.tripProfitabilityPieChartOptions = this.chartOptions.getChartOptions('pie', {
        responsive: true,
        plugins: {
          datalabels:{
            display: false
          },
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Number Of Trips'
          },
        },
        elements: {
          arc: {
            borderWidth: 1
          },
        },
        scales: {
          x: {
            display: false
          },
          y: {
            display: false
          },
        },
      });
    }

  ngOnInit(): void {
    if(this.isUserHasDirectorRole){
      this.setDefaultFilterValues();
      this.filters.getFinanceReportFilter().subscribe((filter: FinanceReportFilterResult) => {
        this.tripProfitabilityDirectorReportFilter.agentName = typeof filter.advisorName === 'string'
          ? filter.advisorName
          : (filter.advisorName as any)?.value || (filter.advisorName as any)?.AgentName || (filter.advisorName as any)?.agentName || '';

        this.tripProfitabilityDirectorReportFilter.tripReference = typeof filter.tripReference === 'string'
          ? filter.tripReference
          : (filter.tripReference as any)?.value || (filter.tripReference as any)?.ExternalReference || (filter.tripReference as any)?.externalReference || '';

        this.tripProfitabilityDirectorReportFilter.bookingStartFrom = filter.bookingStartFrom ? moment(filter.bookingStartFrom).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : "";
        this.tripProfitabilityDirectorReportFilter.bookingStartTo = filter.bookingStartTo ? moment(filter.bookingStartTo).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : "";

        this.tripProfitabilityDirectorReportFilter.bookingEndFrom = filter.bookingEndFrom ? moment(filter.bookingEndFrom).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : "";
        this.tripProfitabilityDirectorReportFilter.bookingEndTo = filter.bookingEndTo ? moment(filter.bookingEndTo).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : "";

        this.tripProfitabilityDirectorReportFilter.dateCreatedFrom = filter.dateCreatedFrom ? moment(filter.dateCreatedFrom).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : "";
        this.tripProfitabilityDirectorReportFilter.dateCreatedTo = filter.dateCreatedTo ? moment(filter.dateCreatedTo).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : "";

        this.tripProfitabilityDirectorReportFilter.pageNumber = 0;

        this.pagination = {
          pageSize: this.pagination.pageSize || 25,
          pageIndex: 0,
          totalItemCount: 0,
        };

        this.pieChartDataInitialized = false;
        this.getTripProfitabilityReport();
      });
    }
  }

  private setDefaultFilterValues(): void {
    this.tripProfitabilityDirectorReportFilter.pageSize = 25;
    this.tripProfitabilityDirectorReportFilter.orderKey = 'Desc';
    this.tripProfitabilityDirectorReportFilter.propertyName = 'dateCreated';
  }

  public ngAfterViewInit(): void {
    if(this.isUserHasDirectorRole){
      this.dataSourceDirector.sort = this.sort;
    }
  }

  getTripProfitabilityReport() {
    if(this.isUserHasDirectorRole){
      this.financeReportsService.getTripProfitabilityReport(this.tripProfitabilityDirectorReportFilter)
      .subscribe((data: TripProfitabilityResponse) => {
        if (data) {
          this.pagination = {
            ... this.pagination,
            pageIndex: data.pagination.pageNumber - 1,
            totalItemCount: data.pagination.totalItemCount,
          };

          this.tripProfitabilityDirectorReportData = data.items;
          this.tripProfitabilityDirectorReportFilter.pageNumber = data.pagination.pageNumber;
          this.dataSourceDirector = new MatTableDataSource(this.tripProfitabilityDirectorReportData);
          this.dataSourceDirector.sort = this.sort;

          if (!this.pieChartDataInitialized) {
            this.loadFullDatasetForChart();
          }
        }
      });
    }
  }

  loadFullDatasetForChart() {
    if (!this.isUserHasDirectorRole) {
      return;
    }

    const fullDataFilter = {
      ...this.tripProfitabilityDirectorReportFilter,
      pageNumber: 0,
      pageSize: 1000
    };

    this.financeReportsService.getTripProfitabilityReport(fullDataFilter)
      .subscribe((fullData: TripProfitabilityResponse) => {
        if (fullData && fullData.items) {
          this.totalMarginRatios.below25 = fullData.items.filter(i => i.marginPercentage <= 25).length;
          this.totalMarginRatios.above25 = fullData.items.filter(i => i.marginPercentage > 25).length;

          this.updatePieChartData();

          this.pieChartDataInitialized = true;
        }
      });
  }

  updatePieChartData() {
    this.tripProfitabilityPieChartData = {
      labels: [
        'Below 25% Margin Percentage',
        'Above 25% Margin Percentage'
      ],
      datasets: [{
        label: 'Trip Profitability Report',
        data: [this.totalMarginRatios.below25, this.totalMarginRatios.above25],
        backgroundColor: [
          '#EDBF6B',
          '#6BC4ED'
        ],
        hoverOffset: 0,
        spacing: 0
      }]
    };
  }

  pageChanged(event: PageEvent): void  {
    if(this.isUserHasDirectorRole){
      this.tripProfitabilityDirectorReportFilter.pageNumber = event.pageIndex + 1;
    }
    this.getTripProfitabilityReport();
  }

  handleSortChange(sort: Sort): void {
    if (!sort.active || sort.direction === '') {
      return;
    }
    if(this.isUserHasDirectorRole){
      this.tripProfitabilityDirectorReportFilter.propertyName = this.getMappedBookingTblPropertyName(sort.active);
      this.tripProfitabilityDirectorReportFilter.orderKey = sort.direction.toUpperCase();
    }
    this.getTripProfitabilityReport();
  }

  downloadGridToFile(): void {
    if(this.isUserHasDirectorRole){
      const maxRowsPerFile = 20000;

      const exportFilter = {
        ...this.tripProfitabilityDirectorReportFilter,
        pageSize: maxRowsPerFile,
        pageNumber: 1
      };

      this.exportGridService.getByDataSource(
        'finance-reports/trip-profitability',
        'trip-profitability-with-agency',
        exportFilter,
        (data)=>{
          const record = data as TripProfitability;
          return {
            'Trip Reference': record?.tripReference,
            'Date Created': this.datePipe.transform(record?.dateCreated, 'M/d/yyyy'),
            'Advisor Name': record?.advisorName,
            'Agent Split': record?.agentSplit,
            'Agency Split': record?.agencySplit,
            'Margin Percentage': record?.marginPercentage,
            'ADX Booking Number': record?.adxBookingNumber
          }
        }
      );
    }
  }

  getMappedBookingTblPropertyName(propertyName: string): string {
    switch (propertyName) {
      case 'tripReference':
        return propertyName;
      case 'dateCreated':
        return propertyName;
      case 'advisorName':
        return 'agent.agentName';
      case 'agentSplit':
        return 'agent.splitRate';
      case 'adxBookingNumber':
        return 'externalBookingNumber';
      default:
        return '';
    }
  }
}
